import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle, Share2, Music2, ArrowRight, ArrowLeft, Check, Lock,
  Loader2, ChevronRight, Sparkles, Link2, Send,
} from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";

const CHANNELS = [
  { id: "whatsapp", icon: MessageCircle, title: "WhatsApp", desc: "Crea mensajes y archivos listos para difundir por WhatsApp.", accent: "text-green-400" },
  { id: "social", icon: Share2, title: "Redes Sociales", desc: "Publica en Facebook e Instagram desde la plataforma.", accent: "text-purple-400" },
  { id: "tiktok", icon: Music2, title: "TikTok", desc: "Próximamente: publicación automática en TikTok.", accent: "text-zinc-500", disabled: true },
];

// Paso 1: elegir el canal de la campaña
export function ChannelSelect({ onSelect, onCancel }) {
  return (
    <div data-testid="channel-select">
      <h2 className="font-display font-bold text-2xl mb-1">1. ¿Por dónde quieres hacer tu marketing?</h2>
      <p className="text-zinc-400 text-sm mb-6">Elige el canal de tu campaña. Te guiaremos paso a paso hasta publicarla.</p>
      <div className="grid sm:grid-cols-3 gap-4">
        {CHANNELS.map((c) => (
          <button
            key={c.id}
            type="button"
            data-testid={`channel-${c.id}`}
            disabled={c.disabled}
            onClick={() => !c.disabled && onSelect(c.id)}
            className={`text-left rounded-2xl border p-6 transition-all ${c.disabled
              ? "border-white/5 bg-[#0e0e0e] opacity-60 cursor-not-allowed"
              : "border-white/10 bg-[#121212] hover:border-purple-500/60 hover:-translate-y-0.5"}`}
          >
            <c.icon className={c.accent} size={28} />
            <div className="flex items-center gap-2 mt-3">
              <h3 className="font-display font-bold text-lg">{c.title}</h3>
              {c.disabled && <span className="text-[10px] uppercase tracking-wider bg-white/5 text-zinc-400 px-2 py-0.5 rounded-full font-bold">Próximamente</span>}
            </div>
            <p className="text-sm text-zinc-400 mt-1">{c.desc}</p>
          </button>
        ))}
      </div>
      <div className="mt-8">
        <Button variant="ghost" onClick={onCancel} className="text-zinc-400"><ArrowLeft size={16} className="mr-2" /> Cancelar</Button>
      </div>
    </div>
  );
}

const ChecklistRow = ({ done, locked, title, desc, to, cta, testid }) => (
  <div data-testid={testid} className={`rounded-2xl border p-5 flex items-center justify-between gap-4 flex-wrap ${done ? "border-green-500/30 bg-green-500/5" : "border-white/10 bg-[#121212]"}`}>
    <div className="flex items-start gap-3 min-w-0">
      <span className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-green-500 text-black" : locked ? "bg-white/5 text-zinc-600" : "bg-purple-600 text-white"}`}>
        {done ? <Check size={15} /> : locked ? <Lock size={13} /> : <ChevronRight size={15} />}
      </span>
      <div className="min-w-0">
        <h3 className="font-semibold text-zinc-100">{title}</h3>
        <p className="text-sm text-zinc-400">{desc}</p>
      </div>
    </div>
    {done ? (
      <span className="text-xs font-semibold text-green-400 flex items-center gap-1.5 shrink-0"><Check size={14} /> Listo</span>
    ) : (
      <Button asChild size="sm" className="bg-purple-600 hover:bg-purple-500 rounded-full px-5 shrink-0">
        <Link data-testid={`${testid}-action`} to={to}>{cta} <ArrowRight size={14} className="ml-1.5" /></Link>
      </Button>
    )}
  </div>
);

const UpcomingRow = ({ icon: Icon, title, desc }) => (
  <div className="rounded-2xl border border-white/5 bg-[#0e0e0e] p-5 flex items-start gap-3 opacity-70">
    <span className="mt-0.5 w-7 h-7 rounded-full bg-white/5 text-zinc-500 flex items-center justify-center shrink-0"><Icon size={14} /></span>
    <div>
      <h3 className="font-semibold text-zinc-300">{title}</h3>
      <p className="text-sm text-zinc-500">{desc}</p>
    </div>
  </div>
);

// Paso 2: checklist de requisitos según el canal elegido
export function Checklist({ canal, onContinue, onBack }) {
  const { user } = useAuth();
  const [companies, setCompanies] = useState(null);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => setCompanies({ companies: [], active_company_id: null }));
    if (canal === "social") {
      api.get("/meta/status").then((r) => setMeta(r.data)).catch(() => setMeta({ connected: false }));
    }
  }, [canal]);

  const isSocial = canal === "social";
  const loading = companies === null || (isSocial && meta === null);

  const waDigits = (user?.whatsapp || "").replace(/\D/g, "");
  const waDone = waDigits.length >= 8;
  const hasCompanies = (companies?.companies || []).length > 0;
  const companyDone = hasCompanies && !!companies?.active_company_id;
  const metaDone = !isSocial || !!meta?.connected;
  const allDone = waDone && companyDone && metaDone;

  const q = `?from=campana&canal=${canal}`;

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-purple-500" size={32} /></div>;
  }

  return (
    <div data-testid="campaign-checklist">
      <h2 className="font-display font-bold text-2xl mb-1">
        2. Prepara tu campaña de {isSocial ? "Redes Sociales" : "WhatsApp"}
      </h2>
      <p className="text-zinc-400 text-sm mb-6">Completa estos requisitos antes de crear el contenido. Cada botón te lleva al lugar correcto y podrás volver aquí.</p>

      <div className="space-y-3">
        <ChecklistRow
          testid="check-whatsapp" done={waDone}
          title="Configura tu WhatsApp"
          desc="Es tu número personal: aquí llegarán tus prospectos calificados."
          to={`/dashboard/perfil${q}`} cta="Configurar WhatsApp"
        />
        <ChecklistRow
          testid="check-company" done={companyDone}
          title="Selecciona tu empresa"
          desc={hasCompanies ? "Elige la empresa con la que la IA creará tu campaña." : "Agrega tu empresa de multinivel (por enlace o manual)."}
          to={hasCompanies ? `/dashboard/empresas${q}` : `/dashboard/perfil${q}`}
          cta={hasCompanies ? "Seleccionar empresa" : "Agregar mi empresa"}
        />
        {isSocial && (
          <ChecklistRow
            testid="check-meta" done={metaDone}
            title="Conecta tus Redes Sociales"
            desc="Vincula tu Facebook e Instagram para publicar automáticamente."
            to={`/dashboard/redes${q}`} cta="Conectar redes"
          />
        )}

        <div className="pt-2 pb-1 text-xs uppercase tracking-wider text-zinc-600 font-bold flex items-center gap-2">
          <Lock size={12} /> Lo que sigue (se desbloquea al terminar lo de arriba)
        </div>
        <UpcomingRow icon={Sparkles} title="Crea la idea y el contenido" desc="La IA genera el texto, las imágenes y el video de tu campaña." />
        <UpcomingRow icon={Link2} title="Conecta tu embudo" desc="Se crea y conecta automáticamente tu página de captura de prospectos." />
        <UpcomingRow icon={Send} title="Publica tu campaña" desc={isSocial ? "Publica en Facebook/Instagram o copia el contenido." : "Difunde por WhatsApp paso a paso."} />
      </div>

      <div className="flex justify-between items-center mt-8 gap-3 flex-wrap">
        <Button variant="ghost" onClick={onBack} className="text-zinc-400"><ArrowLeft size={16} className="mr-2" /> Cambiar canal</Button>
        <Button
          data-testid="checklist-continue" disabled={!allDone} onClick={onContinue}
          className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl px-6 py-5 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Crear contenido de la campaña <ArrowRight size={16} className="ml-2" />
        </Button>
      </div>
      {!allDone && (
        <p data-testid="checklist-pending" className="text-xs text-zinc-500 mt-3 text-right">
          Completa todos los requisitos para continuar.
        </p>
      )}
    </div>
  );
}
